import AnimatedNumber from '../animated-number/AnimatedNumber'
import Icon from '../icon/Icon'
import { useImportedListings } from '../../_hooks/use-imported-listings'
import { useQuickPassProfile } from '../../_hooks/use-quickpass-profile'
import { buildRenterPersonality, buildRenterStatsSummary } from './helpers'
import {
  InsightsCard,
  PersonalityRow,
  PersonalityGlyph,
  PersonalityText,
  PersonalityLabel,
  PersonalityHint,
  StatsGrid,
  StatCell,
  StatLabel,
  StatValue,
  StatHint,
} from './styled'
import { DashboardInsightsProps } from './types'

/**
 * Dashboard insight card that combines a fun renter-personality tag with
 * a stat strip (tracking count, average price, top savings spotted).
 * Renders nothing when neither personality nor any tracked listing
 * exists, so first-time renters don't see an empty card.
 *
 * @param {DashboardInsightsProps} props
 * @returns {JSX.Element | null}
 */
const DashboardInsights = ({
  shouldShow = true,
}: DashboardInsightsProps): JSX.Element | null => {
  const { profile } = useQuickPassProfile()
  const { listings } = useImportedListings()

  if (shouldShow === false) {
    return null
  }

  const personality = buildRenterPersonality(profile)
  const stats = buildRenterStatsSummary(listings)

  if (personality === null && stats.hasMeaningfulData === false) {
    return null
  }

  return (
    <InsightsCard>
      {personality !== null ? (
        <PersonalityRow>
          <PersonalityGlyph aria-hidden="true">
            <Icon icon={personality.icon} />
          </PersonalityGlyph>
          <PersonalityText>
            <PersonalityLabel>{personality.label}</PersonalityLabel>
            <PersonalityHint>{personality.hint}</PersonalityHint>
          </PersonalityText>
        </PersonalityRow>
      ) : null}

      {stats.hasMeaningfulData === true ? (
        <StatsGrid>
          <StatCell>
            <StatLabel>Tracking</StatLabel>
            <StatValue accent="primary">
              <AnimatedNumber value={stats.trackingCount} />
            </StatValue>
            <StatHint>
              {stats.trackingCount === 1 ? 'place' : 'places'} watched daily
            </StatHint>
          </StatCell>
          <StatCell>
            <StatLabel>Average</StatLabel>
            <StatValue>
              {stats.averagePrice === null ? (
                '—'
              ) : (
                <AnimatedNumber
                  value={stats.averagePrice}
                  prefix="$"
                  formatter={value => Math.round(value).toLocaleString('en-CA')}
                />
              )}
            </StatValue>
            <StatHint>across your tracked listings</StatHint>
          </StatCell>
          <StatCell>
            <StatLabel>Best drop</StatLabel>
            <StatValue accent="success">
              {stats.topSavings > 0 ? (
                <AnimatedNumber
                  value={stats.topSavings}
                  prefix="−$"
                  formatter={value => Math.round(value).toLocaleString('en-CA')}
                />
              ) : (
                '—'
              )}
            </StatValue>
            <StatHint>
              {stats.topSavings > 0
                ? 'spotted on a watched place'
                : "we're still watching"}
            </StatHint>
          </StatCell>
        </StatsGrid>
      ) : null}
    </InsightsCard>
  )
}

export default DashboardInsights
